import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { portfolioService } from '../services/portfolioService';
import { PortfolioItem } from '@shared/types';
import { CheckCircleIcon, XIcon } from './icons';

const PortfolioManagementTab: React.FC = () => {
    const { t } = useTranslation();
    const [portfolioData, setPortfolioData] = useState<PortfolioItem[]>([]);

    useEffect(() => {
        loadPortfolioData();
    }, []);

    const loadPortfolioData = async () => {
        const items = await portfolioService.getItems();
        setPortfolioData([...items]); // Fix reference equality bug
    };

    return (
        <motion.div
            key="portfolio"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
        >
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h2 className="text-2xl font-bold dark:text-white">{t('adminDashboard_portfolioTitle')}</h2>
                        <p className="text-sm text-gray-500">Manage service visibility and pricing tiers.</p>
                    </div>
                </div>

                <div className="space-y-4">
                    {portfolioData.map((item) => (
                        <div key={item.id} className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-4">
                                    <img src={item.imageUrl} alt="" className="w-12 h-12 rounded-xl object-cover" />
                                    <div>
                                        <h3 className="font-bold dark:text-white">{t(item.titleKey)}</h3>
                                        <p className="text-xs text-gray-500 uppercase tracking-widest">{t(item.categoryKey) || item.categoryKey}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-2 mr-4 border-r border-gray-100 dark:border-gray-700 pr-4">
                                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${item.isOnlineService ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}>
                                            {item.isOnlineService ? 'Online' : 'In-Person'}
                                        </span>
                                        <button
                                            onClick={async () => {
                                                await portfolioService.updateOnlineServiceStatus(item.id, !item.isOnlineService);
                                                await loadPortfolioData();
                                            }}
                                            className="px-3 py-1 text-xs font-bold rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 transition-all border border-gray-200 dark:border-gray-600"
                                            title="Toggle Online/In-Person Service"
                                        >
                                            Toggle
                                        </button>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${item.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                            {item.isActive ? t('adminDashboard_portfolioActive') : t('adminDashboard_portfolioInactive')}
                                        </span>
                                        <button
                                            onClick={async () => {
                                                await portfolioService.updateItemVisibility(item.id, !item.isActive);
                                                await loadPortfolioData();
                                            }}
                                            className={`p-2 rounded-xl transition-all ${item.isActive ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}
                                            title="Toggle Visibility"
                                        >
                                            {item.isActive ? <XIcon className="w-5 h-5" /> : <CheckCircleIcon className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100 dark:border-gray-800">
                                {item.pricingTiers.map((tier) => (
                                    <div key={tier.id} className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
                                        <p className="text-[10px] font-black text-gray-400 uppercase mb-2">{t(tier.nameKey)}</p>
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-400 font-bold">€</span>
                                            <input
                                                type="number"
                                                defaultValue={tier.basePriceEUR}
                                                title={`${t(item.titleKey)} - ${t(tier.nameKey)} Price`}
                                                onBlur={async (e) => {
                                                    const val = parseFloat(e.target.value);
                                                    if (!isNaN(val)) {
                                                        await portfolioService.updateTierPrice(item.id, tier.id, val);
                                                        await loadPortfolioData();
                                                    }
                                                }}
                                                className="w-full bg-transparent border-none p-0 font-bold text-gray-900 dark:text-white focus:ring-0"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </motion.div>
    );
};

export default PortfolioManagementTab;
